import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Capacitor, PluginListenerHandle } from '@capacitor/core';
import { ScreenOrientation } from '@capacitor/screen-orientation';

export interface VideoOrientationState {
  landscape: boolean;
  /** Non-zero when iOS keeps the WebView in portrait and the video must be CSS-rotated. */
  cssRotateDeg: 0 | 90 | -90;
}

/** Portrait for the app; temporary unlock/landscape only for historique video playback. */
@Injectable({ providedIn: 'root' })
export class VideoOrientationService implements OnDestroy {
  private listener: PluginListenerHandle | null = null;
  private landscapeHandler: ((state: VideoOrientationState) => void) | null = null;
  private videoRotationEnabled = false;
  private lastDeviceOrientationEmit = 0;

  constructor(private ngZone: NgZone) {}

  ngOnDestroy(): void {
    void this.disableVideoRotation();
  }

  async lockAppPortrait(): Promise<void> {
    if (!this.canUseNative()) {
      this.lockWebPortrait();
      return;
    }
    try {
      await ScreenOrientation.lock({ orientation: 'portrait' });
    } catch {
      // ignore — some WebViews reject repeated locks
    }
  }

  /**
   * Let the OS rotate according to the phone auto-rotate setting
   * (used while native HTML5 video fullscreen is active).
   */
  async unlockOrientation(): Promise<void> {
    this.unlockWebOrientation();
    if (!this.canUseNative()) {
      return;
    }
    try {
      await ScreenOrientation.unlock();
    } catch {
      // ignore
    }
  }

  /** Relock portrait unless historique camera playback still needs rotation. */
  async restorePortraitIfIdle(): Promise<void> {
    if (this.videoRotationEnabled) {
      return;
    }
    await this.lockAppPortrait();
  }

  async lockVideoLandscape(): Promise<void> {
    if (!this.canUseNative()) {
      return;
    }
    try {
      await ScreenOrientation.lock({ orientation: 'landscape' });
    } catch {
      // ignore
    }
  }

  /** Allow rotation while the historique video player / fullscreen clip is open. */
  async enableVideoRotation(onLandscapeChange: (state: VideoOrientationState) => void): Promise<void> {
    this.landscapeHandler = onLandscapeChange;
    this.videoRotationEnabled = true;

    await this.requestIosMotionPermission();
    await this.unlockOrientation();

    if (this.canUseNative()) {
      try {
        this.listener = await ScreenOrientation.addListener('screenOrientationChange', () => {
          void this.emitLandscapeState();
        });
      } catch {
        // fall back to viewport / deviceorientation
      }
    }

    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('orientationchange', this.onViewportChange);
    if (Capacitor.getPlatform() === 'ios') {
      window.addEventListener('deviceorientation', this.onDeviceOrientation, true);
    }
    await this.emitLandscapeState();
  }

  async disableVideoRotation(): Promise<void> {
    this.videoRotationEnabled = false;
    this.landscapeHandler = null;
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('orientationchange', this.onViewportChange);
    window.removeEventListener('deviceorientation', this.onDeviceOrientation, true);

    if (this.listener) {
      await this.listener.remove();
      this.listener = null;
    }

    await this.lockAppPortrait();
  }

  private readonly onViewportChange = (): void => {
    void this.emitLandscapeState();
  };

  private readonly onDeviceOrientation = (event: DeviceOrientationEvent): void => {
    if (!this.videoRotationEnabled || !this.landscapeHandler) {
      return;
    }
    const now = Date.now();
    if (now - this.lastDeviceOrientationEmit < 120) {
      return;
    }
    const gamma = event.gamma;
    const beta = event.beta;
    if (gamma == null || beta == null) {
      return;
    }
    const absGamma = Math.abs(gamma);
    const absBeta = Math.abs(beta);
    const landscape = absGamma > 35 && absGamma > absBeta * 0.55;
    const cssRotateDeg = this.cssRotateForLandscape(
      landscape,
      gamma < 0 ? 'landscape-secondary' : 'landscape-primary'
    );
    this.lastDeviceOrientationEmit = now;
    this.ngZone.run(() => {
      this.landscapeHandler?.({ landscape, cssRotateDeg });
    });
  };

  private async emitLandscapeState(): Promise<void> {
    if (!this.videoRotationEnabled || !this.landscapeHandler) {
      return;
    }
    const type = await this.currentOrientationType();
    const landscape = type.startsWith('landscape');
    const cssRotateDeg = this.cssRotateForLandscape(landscape, type);
    this.ngZone.run(() => {
      this.landscapeHandler?.({ landscape, cssRotateDeg });
    });
  }

  private cssRotateForLandscape(landscape: boolean, type: string): 0 | 90 | -90 {
    if (!landscape) {
      return 0;
    }
    const viewportAlreadyLandscape = window.innerWidth > window.innerHeight;
    if (viewportAlreadyLandscape) {
      return 0;
    }
    return type === 'landscape-secondary' ? -90 : 90;
  }

  private async currentOrientationType(): Promise<string> {
    if (this.canUseNative()) {
      try {
        const { type } = await ScreenOrientation.orientation();
        return type;
      } catch {
        // fall through
      }
    }
    if (typeof window.orientation === 'number') {
      const angle = Math.abs(window.orientation);
      if (angle === 90) {
        return window.orientation < 0 ? 'landscape-secondary' : 'landscape-primary';
      }
    }
    return window.innerWidth > window.innerHeight ? 'landscape-primary' : 'portrait-primary';
  }

  private async requestIosMotionPermission(): Promise<void> {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }
    const DeviceOrientation = window.DeviceOrientationEvent as unknown as {
      requestPermission?: () => Promise<string>;
    };
    if (typeof DeviceOrientation?.requestPermission !== 'function') {
      return;
    }
    try {
      await DeviceOrientation.requestPermission();
    } catch {
      // user denied or WebView does not require it
    }
  }

  private canUseNative(): boolean {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('ScreenOrientation');
  }

  private lockWebPortrait(): void {
    const orientation = screen.orientation as { lock?: (o: OrientationLockType) => Promise<void> };
    if (!orientation?.lock) {
      return;
    }
    void orientation.lock('portrait').catch(() => undefined);
  }

  private unlockWebOrientation(): void {
    try {
      screen.orientation?.unlock?.();
    } catch {
      // ignore
    }
  }
}
